import { LogIn } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { useToast } from '../components/ui/Toast';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { MdsLogo } from '../components/MdsLogo';
import type { Role } from '../auth/roles';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    role: Role;
    customerId: string | null;
    name: string | null;
    mustChangePassword: boolean;
  };
}

/** Screens 1-2 — Login. Backend issues 15-min access + 7-day refresh (Section 27.17). */
export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const fetchCaps = useCapabilitiesStore((s) => s.fetch);
  const { showApiError } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ data: LoginResponse }>('/auth/login', {
        email,
        password,
      });
      const r = data.data;
      localStorage.setItem('refresh_token', r.refreshToken);
      setSession(r.accessToken, {
        sub: r.user.id,
        role: r.user.role,
        customerId: r.user.customerId,
        email: r.user.email,
        mustChangePassword: r.user.mustChangePassword,
      });
      // Forced first-login change: skip loading capabilities (the API would 403 anyway) and
      // route straight to the change-password screen.
      if (r.user.mustChangePassword) {
        navigate('/change-password', { replace: true });
        return;
      }
      await fetchCaps();
      navigate('/');
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-canvas p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2 text-center">
          <div className="mds flex justify-center">
            <MdsLogo />
          </div>
          <CardDescription>{t('auth.signInToContinue')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('common.email')}</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              <LogIn className="h-4 w-4" />
              {busy ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              <Link to="/forgot-password" className="hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
